import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    CircularProgress,
    Alert,
    Chip,
    alpha,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Collapse,
    Paper,
} from '@mui/material';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TimerIcon from '@mui/icons-material/Timer';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { clearStoredAuthToken, getStoredAuthToken } from '../services/auth-service';
import { Envs } from '../conf/envs';

// GraphQL Queries and Mutations
const AUTH_STATUS_QUERY = gql`
    query GetTOConlineAuthStatus {
        toconlineAuthStatus {
            authenticated
            token_info {
                token_type
                expires_at
                expires_in_seconds
                is_expired
            }
            error
        }
    }
`;

const GET_AUTH_URL_QUERY = gql`
    query GetTOConlineAuthUrl {
        toconlineAuthUrl {
            authorization_url
            message
        }
    }
`;

const CONNECT_MUTATION = gql`
    mutation ConnectTOConline {
        toconlineConnect {
            success
            message
        }
    }
`;

// Manual connection mutation (used for direct API connection without OAuth flow)
const MANUAL_CONNECT_MUTATION = gql`
    mutation ManualConnectTOConline {
        toconlineConnect {
            success
            message
        }
    }
`;

const REVOKE_AUTH_MUTATION = gql`
    mutation RevokeAuth {
        toconlineRevokeAuth {
            success
            message
        }
    }
`;

export default function TOConlineAuth() {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [error, setError] = useState(null);
    const [errorOpen, setErrorOpen] = useState(false);
    const [shouldFetchAuthUrl, setShouldFetchAuthUrl] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(null);
    const pathname = window.location.pathname;
    const token = getStoredAuthToken();
    const authPollRef = useRef(null);
    const getAllowedOAuthOrigins = useCallback(() => {
        const origins = new Set([window.location.origin]);
        const configuredOrigins = (process.env.REACT_APP_TOCONLINE_CALLBACK_ORIGINS || "")
            .split(",")
            .map((origin) => origin.trim())
            .filter(Boolean);

        configuredOrigins.forEach((origin) => origins.add(origin));

        try {
            origins.add(new URL(Envs.API_BASE || window.location.origin, window.location.origin).origin);
        } catch (err) {
            // Ignore malformed API_BASE; same-origin remains allowed.
        }

        return origins;
    }, []);

    // Show error alert when error state changes
    useEffect(() => {
        if (error) {
            setErrorOpen(true);
        } else {
            setErrorOpen(false);
        }
    }, [error]);

    // Function to handle unauthorized errors safely
    const handleUnauthorized = useCallback(() => {
        clearStoredAuthToken();
        if (pathname !== "/login") {
            navigate('/login');
        }
    }, [navigate, pathname]);

    // Query to check current auth status
    const {
        data: authStatus,
        loading: authLoading,
        refetch: refetchAuthStatus,
        error: authStatusError
    } = useQuery(AUTH_STATUS_QUERY, {
        skip: !token, // Skip if no token exists
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        pollInterval: 15000,
    });

    // Force an immediate refetch on mount when token exists
    useEffect(() => {
        if (token) {
            refetchAuthStatus();
        }
    }, [token, refetchAuthStatus]);

    // Handle unauthorized errors
    useEffect(() => {
        if (authStatusError?.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
            handleUnauthorized();
        } else if (authStatusError) {
            setError(t("TOCONLINE.ERROR_CHECK_STATUS", { message: authStatusError.message }));
        }
    }, [authStatusError, handleUnauthorized, t]);

    // Query to get auth URL (with skip option)
    const {
        data: authUrlData,
        loading: authUrlLoading,
        refetch: refetchAuthUrl,
        error: authUrlError
    } = useQuery(GET_AUTH_URL_QUERY, {
        skip: !shouldFetchAuthUrl || !token, // Skip if no token exists or shouldn't fetch
        onCompleted: (data) => {
            if (data?.toconlineAuthUrl?.authorization_url) {
                try {
                    const popup = window.open(data.toconlineAuthUrl.authorization_url, 'toconline_oauth', 'width=600,height=700');

                    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                        throw new Error(t("TOCONLINE.ERROR_POPUP_BLOCKED"));
                    }

                    const allowedOrigins = getAllowedOAuthOrigins();
                    // Add message event listener for OAuth callback communication
                    const messageHandler = (event) => {
                        if (event.source !== popup || !allowedOrigins.has(event.origin)) {
                            return;
                        }

                        if (event.data && (event.data.type === 'oauth_success' || event.data.type === 'oauth_error')) {
                            if (event.data.type === 'oauth_success') {
                                // Success - refresh auth status
                                refetchAuthStatus();
                                setIsConnecting(false);
                                setError(null);
                            } else if (event.data.type === 'oauth_error') {
                                // Error
                                setError(event.data.error || t("TOCONLINE.ERROR_OAUTH_FAILED"));
                                setIsConnecting(false);
                            }

                            // Remove event listener after handling
                            window.removeEventListener('message', messageHandler);
                        }
                    };

                    // Add event listener
                    window.addEventListener('message', messageHandler);

                    // Set timeout to remove listener if no response
                    setTimeout(() => {
                        window.removeEventListener('message', messageHandler);
                        if (isConnecting) {
                            setIsConnecting(false);
                            setError(t("TOCONLINE.ERROR_OAUTH_TIMEOUT"));
                        }
                    }, 300000); // 5 minutes timeout
                } catch (error) {
                    setError(t("TOCONLINE.ERROR_OPEN_POPUP", { message: error.message }));
                    setIsConnecting(false);
                }
            }
            setShouldFetchAuthUrl(false);
        }
    });

    // Check for unauthorized errors in auth URL query
    useEffect(() => {
        if (authUrlError?.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
            handleUnauthorized();
        } else if (authUrlError) {
            setError(t("TOCONLINE.ERROR_AUTH_URL", { message: authUrlError.message }));
            setIsConnecting(false);
        }
    }, [authUrlError, handleUnauthorized, t]);

    // Keep a live countdown based on expires_at from backend
    useEffect(() => {
        const tokenInfo = authStatus?.toconlineAuthStatus?.token_info;
        if (!tokenInfo?.expires_at) {
            setRemainingSeconds(null);
            return;
        }

        const computeRemaining = () => {
            const target = new Date(tokenInfo.expires_at).getTime();
            if (Number.isNaN(target)) {
                setRemainingSeconds(null);
                return;
            }
            const diff = Math.max(0, Math.floor((target - Date.now()) / 1000));
            setRemainingSeconds(diff);
        };

        computeRemaining();
        const interval = setInterval(computeRemaining, 1000);
        return () => clearInterval(interval);
    }, [authStatus?.toconlineAuthStatus?.token_info]);

    // Mutation to connect
    const [connectToTOConline, { error: connectError }] = useMutation(CONNECT_MUTATION);

    // Mutation to revoke auth
    const [revokeAuth, { error: revokeError }] = useMutation(REVOKE_AUTH_MUTATION);

    // Check for unauthorized errors in connect mutation
    useEffect(() => {
        if (connectError?.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
            handleUnauthorized();
        } else if (connectError) {
            setError(t("TOCONLINE.ERROR_CONNECT", { message: connectError.message }));
            setIsConnecting(false);
        }
    }, [connectError, handleUnauthorized, t]);

    // Check for unauthorized errors in revoke mutation
    useEffect(() => {
        if (revokeError?.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
            handleUnauthorized();
        } else if (revokeError) {
            setError(t("TOCONLINE.ERROR_REVOKE", { message: revokeError.message }));
            setIsDisconnecting(false);
        }
    }, [revokeError, handleUnauthorized, t]);

    const startAuthProcess = () => {
        // Don't proceed if there's an error
        if (error) {
            setErrorOpen(true);
            return;
        }

        setIsConnecting(true);
        setError(null);
        setShouldFetchAuthUrl(true);
    };

    // Poll backend auth status while waiting for popup callback, so we don't get stuck if postMessage fails
    useEffect(() => {
        if (!isConnecting) {
            if (authPollRef.current) {
                clearInterval(authPollRef.current);
                authPollRef.current = null;
            }
            return;
        }

        const startedAt = Date.now();

        authPollRef.current = setInterval(async () => {
            try {
                const refreshed = await refetchAuthStatus();
                const status = refreshed?.data?.toconlineAuthStatus;
                if (status?.authenticated && !status?.token_info?.is_expired) {
                    setIsConnecting(false);
                    setError(null);
                    clearInterval(authPollRef.current);
                    authPollRef.current = null;
                } else if (Date.now() - startedAt > 5 * 60 * 1000) {
                    setError(t("TOCONLINE.ERROR_OAUTH_TIMEOUT"));
                    setIsConnecting(false);
                    clearInterval(authPollRef.current);
                    authPollRef.current = null;
                }
            } catch (err) {
                // Ignore transient polling errors while auth popup is in flight.
            }
        }, 4000);

        return () => {
            if (authPollRef.current) {
                clearInterval(authPollRef.current);
                authPollRef.current = null;
            }
        };
    }, [isConnecting, refetchAuthStatus, t]);

    // This function is no longer needed as we handle the OAuth flow through the message event listener
    // Keeping a simplified version for backward compatibility
    const handleConnect = async () => {
        startAuthProcess();
    };

    // Handle disconnect
    const handleDisconnect = async () => {
        // Don't proceed if there's an error
        if (error) {
            setErrorOpen(true);
            return;
        }

        setIsDisconnecting(true);
        setError(null);

        try {
            const { data: revokeData } = await revokeAuth();

            if (revokeData?.toconlineRevokeAuth?.success) {
                await refetchAuthStatus();
            } else {
                throw new Error(revokeData?.toconlineRevokeAuth?.message || t("TOCONLINE.ERROR_DISCONNECT_FAILED"));
            }

        } catch (error) {
            if (error?.graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) {
                handleUnauthorized();
            } else {
                setError(error.message);
            }
        } finally {
            setIsDisconnecting(false);
            setConfirmDialogOpen(false);
        }
    };

    // Open confirmation dialog
    const openDisconnectDialog = () => {
        // Don't proceed if there's an error
        if (error) {
            setErrorOpen(true);
            return;
        }

        setConfirmDialogOpen(true);
    };

    // Loading state
    if (authLoading) {
        return (
            <Card elevation={0} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography>{t("TOCONLINE.CHECKING_STATUS")}</Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    const isAuthenticated = authStatus?.toconlineAuthStatus?.authenticated;
    const tokenInfo = authStatus?.toconlineAuthStatus?.token_info;
    const safeRemainingSeconds =
        remainingSeconds ??
        (typeof tokenInfo?.expires_in_seconds === 'number' ? tokenInfo.expires_in_seconds : null);
    const remainingMins =
        typeof safeRemainingSeconds === 'number' && Number.isFinite(safeRemainingSeconds)
            ? Math.floor(safeRemainingSeconds / 60)
            : null;

    return (
        <Card
            elevation={0}
            sx={{
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
                backdropFilter: 'blur(8px)',
            }}
        >
            <CardContent>
                <Stack spacing={2}>
                    {/* Error Alert */}
                    <Collapse in={errorOpen}>
                        <Alert
                            severity="error"
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setErrorOpen(false);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="subtitle2">{t("TOCONLINE.INTEGRATION_ERROR_TITLE")}</Typography>
                            <Typography variant="body2">{error}</Typography>
                        </Alert>
                    </Collapse>

                    {/* Status Header */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {t("TOCONLINE.INTEGRATION_TITLE")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("TOCONLINE.INTEGRATION_DESC")}
                            </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Chip
                            icon={isAuthenticated ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={isAuthenticated ? t("STATUS.CONNECTED") : t("STATUS.DISCONNECTED")}
                            color={isAuthenticated ? 'success' : 'error'}
                            variant="outlined"
                        />
                    </Stack>

                    {/* Connection Status and Actions */}
                    {isAuthenticated && tokenInfo ? (
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t("TOCONLINE.CONNECTION_STATUS")}
                            </Typography>
                            <Stack spacing={1}>
                                <Typography variant="body2">
                                    <strong>{t("TOCONLINE.TOKEN_TYPE")}</strong> {tokenInfo.token_type}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t("TOCONLINE.EXPIRES_AT")}</strong> {formatDate(tokenInfo.expires_at)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t("TOCONLINE.REMAINING_TIME")}</strong> {remainingMins !== null ? t("TOCONLINE.MINUTES", { count: remainingMins }) : t("COMMON.NA")}
                                </Typography>
                                <Alert
                                    severity={
                                        typeof safeRemainingSeconds === 'number' && safeRemainingSeconds < 3600
                                            ? "warning"
                                            : "info"
                                    }
                                    icon={<TimerIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    {typeof safeRemainingSeconds === 'number' && safeRemainingSeconds < 3600
                                        ? t("TOCONLINE.TOKEN_EXPIRING")
                                        : t("TOCONLINE.TOKEN_VALID")}
                                </Alert>

                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<LinkOffIcon />}
                                    onClick={openDisconnectDialog}
                                    disabled={isDisconnecting || isConnecting || !!error}
                                >
                                    {isDisconnecting ? t("ACTIONS.DISCONNECTING") : t("ACTIONS.DISCONNECT")}
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t("TOCONLINE.NOT_CONNECTED")}
                            </Typography>
                            <Typography variant="body2" paragraph>
                                {t("TOCONLINE.NOT_CONNECTED_DESC")}
                            </Typography>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<LinkIcon />}
                                onClick={startAuthProcess}
                                disabled={isConnecting || isDisconnecting || !!error}
                            >
                                {isConnecting ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                        {t("TOCONLINE.CONNECTING")}
                                    </>
                                ) : (
                                    t("TOCONLINE.CONNECT")
                                )}
                            </Button>
                        </Box>
                    )}

                    {/* API Status Indicators */}
                    {authUrlLoading && (
                        <Alert severity="info" icon={<CircularProgress size={16} />}>
                            {t("TOCONLINE.GETTING_AUTH_URL")}
                        </Alert>
                    )}
                </Stack>
            </CardContent>

            {/* Disconnect Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>{t("TOCONLINE.CONFIRM_DISCONNECT")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("TOCONLINE.DISCONNECT_CONFIRM_DESC")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>{t("ACTIONS.CANCEL")}</Button>
                    <Button onClick={handleDisconnect} color="error" disabled={isDisconnecting}>
                        {isDisconnecting ? t("ACTIONS.DISCONNECTING") : t("ACTIONS.DISCONNECT")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );

    function formatDate(dateString) {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            return dateString;
        }
    }
} 
